import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, uploadDocument } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowUpDown, FileText, Loader2, Paperclip, Pencil, Plus, Search, Trash2, X } from "lucide-react";

const CATEGORIES = ["Co-Packing", "Freight", "Rent", "Utilities", "Equipment & Repairs", "Professional Services", "Insurance", "Storage", "Supplies", "Other"];

const PAY_META = {
  unpaid: { label: "Unpaid", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  partially_paid: { label: "Partially Paid", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  paid: { label: "Paid", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
const STATUS_META = {
  open: { label: "Open", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

const money = (v) => "$" + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(`${d}T00:00:00`);
  return isNaN(dt) ? d : dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

function ExpenseDialog({ open, onClose, initial, copackers, onSaved }) {
  const empty = { vendor_type: "other", copacker_id: "", vendor_name: "", category: "", description: "", order_date: "", due_date: "", amount: "", currency: "USD", payment_status: "unpaid", status: "open", notes: "", docs: [] };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...empty, ...initial, amount: initial.amount ? String(initial.amount) : "" } : { ...empty, order_date: new Date().toISOString().slice(0, 10) });
  }, [open, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const label = "text-[11px] font-semibold uppercase tracking-wide text-slate-500";

  const addDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const up = await uploadDocument(file);
      set("docs", [...(form.docs || []), { name: file.name, path: up.path || "", url: up.url || "" }]);
      toast.success("Document attached");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    }
    setUploading(false);
  };

  const save = async () => {
    if (form.vendor_type === "copacker" && !form.copacker_id) { toast.error("Select a co-packer"); return; }
    if (form.vendor_type === "other" && !form.vendor_name.trim()) { toast.error("Enter a vendor name"); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount) || 0 };
      if (payload.vendor_type === "copacker") {
        const cp = copackers.find((c) => c.id === payload.copacker_id);
        payload.vendor_name = cp?.company_name || payload.vendor_name || "Co-packer";
      }
      if (initial?.id) await api.put(`/expense-orders/${initial.id}`, payload);
      else await api.post("/expense-orders", payload);
      toast.success(initial?.id ? "Expense updated" : "Expense added");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save expense");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-sm" data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold">{initial?.id ? `Edit ${initial.exp_number}` : "New Operational Expense"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className={label}>Vendor</Label>
              <Select value={form.vendor_type === "copacker" ? form.copacker_id : "__other"} onValueChange={(v) => {
                if (v === "__other") { set("vendor_type", "other"); set("copacker_id", ""); }
                else setForm((f) => ({ ...f, vendor_type: "copacker", copacker_id: v }));
              }}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="expense-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  {copackers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name} (Co-packer)</SelectItem>)}
                  <SelectItem value="__other">Other vendor…</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.vendor_type === "other" && (
              <div className="space-y-1.5">
                <Label className={label}>Vendor name</Label>
                <Input value={form.vendor_name} onChange={(e) => set("vendor_name", e.target.value)} placeholder="Vendor name" className="h-9 rounded-sm" data-testid="expense-vendor-name" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className={label}>Category</Label>
              <Select value={form.category || undefined} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="expense-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className={label}>Description</Label>
              <Input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="What is this expense for?" className="h-9 rounded-sm" data-testid="expense-description" />
            </div>
            <div className="space-y-1.5">
              <Label className={label}>Expense date</Label>
              <Input type="date" value={form.order_date} onChange={(e) => set("order_date", e.target.value)} className="h-9 rounded-sm" data-testid="expense-order-date" />
            </div>
            <div className="space-y-1.5">
              <Label className={label}>Payment due</Label>
              <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="h-9 rounded-sm" data-testid="expense-due-date" />
            </div>
            <div className="space-y-1.5">
              <Label className={label}>Amount (USD)</Label>
              <Input type="text" inputMode="decimal" value={form.amount} onChange={(e) => set("amount", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0.00" className="h-9 rounded-sm" data-testid="expense-amount" />
            </div>
            <div className="space-y-1.5">
              <Label className={label}>Payment status</Label>
              <Select value={form.payment_status} onValueChange={(v) => set("payment_status", v)}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="expense-payment-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PAY_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className={label}>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="rounded-sm" data-testid="expense-notes" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className={label}>Documents (invoice, receipt…)</Label>
            <div className="flex flex-wrap items-center gap-2">
              {(form.docs || []).map((d, i) => (
                <span key={`${d.name}-${i}`} className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700">
                  <FileText className="h-3 w-3 text-slate-400" /> {d.name || "Document"}
                  <button type="button" onClick={() => set("docs", form.docs.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500" data-testid={`expense-doc-remove-${i}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
              <label className="inline-flex cursor-pointer items-center gap-1 rounded-sm border border-dashed border-slate-300 px-2 py-1 text-xs text-slate-500 hover:border-[#002FA7] hover:text-[#002FA7]">
                {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />} Attach
                <input type="file" className="hidden" onChange={(e) => { addDoc(e.target.files?.[0]); e.target.value = ""; }} data-testid="expense-doc-input" />
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="expense-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="expense-save">
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{initial?.id ? "Save Changes" : "Add Expense"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function OperationalExpenses() {
  const [items, setItems] = useState([]);
  const [copackers, setCopackers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [fCategory, setFCategory] = useState("all");
  const [fVendor, setFVendor] = useState("all");
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    try {
      const [ex, cp] = await Promise.all([api.get("/expense-orders"), api.get("/co-packers")]);
      setItems(ex.data);
      setCopackers(cp.data);
    } catch {
      toast.error("Couldn't load expenses");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const vendors = useMemo(() => [...new Set(items.map((e) => e.vendor_name).filter(Boolean))].sort(), [items]);
  const categories = useMemo(() => [...new Set(items.map((e) => e.category).filter(Boolean))].sort(), [items]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let arr = items.filter((e) => {
      if (tab === "unpaid" && (e.payment_status === "paid" || e.status === "cancelled")) return false;
      if (tab === "paid" && e.payment_status !== "paid") return false;
      if (tab === "cancelled" && e.status !== "cancelled") return false;
      if (tab !== "cancelled" && tab !== "all" && e.status === "cancelled") return false;
      if (fCategory !== "all" && e.category !== fCategory) return false;
      if (fVendor !== "all" && e.vendor_name !== fVendor) return false;
      if (term) {
        const hay = `${e.exp_number} ${e.vendor_name} ${e.category} ${e.description} ${e.notes}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      if (sort.key === "vendor") return dir * a.vendor_name.localeCompare(b.vendor_name);
      if (sort.key === "amount") return dir * ((a.amount || 0) - (b.amount || 0));
      return dir * String(a.order_date || "").localeCompare(String(b.order_date || ""));
    });
    return arr;
  }, [items, tab, q, fCategory, fVendor, sort]);

  const total = useMemo(() => filtered.reduce((s, e) => s + (e.status === "cancelled" ? 0 : (e.amount || 0)), 0), [filtered]);
  const unpaidTotal = useMemo(() => filtered.reduce((s, e) => s + (e.status !== "cancelled" && e.payment_status !== "paid" ? (e.amount || 0) : 0), 0), [filtered]);

  const counts = useMemo(() => ({
    all: items.length,
    unpaid: items.filter((e) => e.payment_status !== "paid" && e.status !== "cancelled").length,
    paid: items.filter((e) => e.payment_status === "paid" && e.status !== "cancelled").length,
    cancelled: items.filter((e) => e.status === "cancelled").length,
  }), [items]);

  const patch = async (id, fields) => {
    try {
      const res = await api.patch(`/expense-orders/${id}/meta`, { fields });
      setItems((prev) => prev.map((x) => (x.id === id ? res.data : x)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't update");
    }
  };

  const del = async () => {
    try {
      await api.delete(`/expense-orders/${toDelete.id}`);
      setItems((prev) => prev.filter((x) => x.id !== toDelete.id));
      toast.success("Expense deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
    setToDelete(null);
  };

  const sortBtn = (key, lbl) => (
    <button type="button" onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }))}
      className={`inline-flex items-center gap-0.5 ${sort.key === key ? "text-[#002FA7]" : "hover:text-slate-600"}`} data-testid={`exp-sort-${key}`}>
      {lbl} <ArrowUpDown className="h-3 w-3" />
    </button>
  );

  const TABS = [["all", "All"], ["unpaid", "Unpaid"], ["paid", "Paid"], ["cancelled", "Cancelled"]];

  return (
    <main className="mx-auto max-w-full px-4 py-8 sm:px-6 md:px-8 md:py-10" data-testid="operational-expenses-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Operations</p>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">Operational Expenses</h1>
        </div>
        <Button onClick={() => setDialog({ open: true, initial: null })} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="new-expense-button">
          <Plus className="mr-1.5 h-4 w-4" /> New Expense
        </Button>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="inline-flex flex-wrap rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid="expense-tabs">
          {TABS.map(([k, lbl]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={`rounded-sm px-3 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              data-testid={`expense-tab-${k}`}>
              {lbl} <span className="ml-1 text-xs font-normal text-slate-400">{counts[k]}</span>
            </button>
          ))}
        </div>
        <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search expenses" className="h-9 rounded-sm pl-8" data-testid="expense-search" />
        </div>
        <Select value={fVendor} onValueChange={setFVendor}>
          <SelectTrigger className="h-9 w-[170px] rounded-sm" data-testid="expense-filter-vendor"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All vendors</SelectItem>{vendors.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fCategory} onValueChange={setFCategory}>
          <SelectTrigger className="h-9 w-[170px] rounded-sm" data-testid="expense-filter-category"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          <span>Sort:</span>{sortBtn("date", "Date")}{sortBtn("vendor", "Vendor")}{sortBtn("amount", "Amount")}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-1 border border-slate-200 bg-slate-50/60 px-4 py-2.5 text-sm" data-testid="expense-totals">
        <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{tab === "all" ? "All expenses" : TABS.find(([k]) => k === tab)?.[1]}</span>
        <span className="font-heading text-lg font-black text-slate-900" data-testid="expense-total">{money(total)}</span>
        <span className="text-xs text-slate-500">total{fVendor !== "all" || fCategory !== "all" || q ? " (filtered)" : ""}</span>
        {unpaidTotal > 0 && tab !== "paid" && (
          <span className="text-xs font-semibold text-rose-600" data-testid="expense-unpaid-total">{money(unpaidTotal)} unpaid</span>
        )}
        <span className="ml-auto text-xs text-slate-400">{filtered.length} expense{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 py-20 text-center text-sm text-slate-400" data-testid="expense-empty">No expenses yet. Click "New Expense" to add one.</div>
      ) : (
        <div className="max-h-[calc(100vh-150px)] overflow-auto border border-slate-200" data-testid="expense-table">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-20">
              <tr className="bg-slate-50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                <th className="px-3 py-3">Vendor</th>
                <th className="border-l border-slate-200 px-3 py-3">Date</th>
                <th className="border-l border-slate-200 px-3 py-3">Due</th>
                <th className="border-l border-slate-200 px-3 py-3">Expense #</th>
                <th className="border-l border-slate-200 px-3 py-3">Category</th>
                <th className="border-l border-slate-200 px-3 py-3 min-w-[220px]">Description</th>
                <th className="border-l border-slate-200 px-3 py-3">Documents</th>
                <th className="border-l border-slate-200 px-3 py-3 text-right">Amount</th>
                <th className="border-l border-slate-200 px-3 py-3">Payment</th>
                <th className="border-l border-slate-200 px-3 py-3">Status</th>
                <th className="border-l border-slate-200 px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} className={`border-t border-slate-100 align-top hover:bg-slate-50/50 ${e.status === "cancelled" ? "opacity-50" : ""}`} data-testid={`expense-row-${e.id}`}>
                  <td className="px-3 py-2.5">
                    <span className="font-semibold text-slate-800">{e.vendor_name}</span>
                    {e.vendor_type === "copacker" && <span className="ml-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-indigo-600">Co-packer</span>}
                  </td>
                  <td className="border-l border-slate-100 px-3 py-2.5 whitespace-nowrap text-slate-600">{fmtDate(e.order_date)}</td>
                  <td className="border-l border-slate-100 px-3 py-2.5 whitespace-nowrap text-slate-600">{fmtDate(e.due_date)}</td>
                  <td className="border-l border-slate-100 px-3 py-2.5 font-semibold text-[#002FA7]">{e.exp_number}</td>
                  <td className="border-l border-slate-100 px-3 py-2.5 text-slate-600">{e.category || "—"}</td>
                  <td className="border-l border-slate-100 px-3 py-2.5 text-slate-600">{e.description || "—"}{e.notes && <div className="mt-0.5 text-xs text-slate-400">{e.notes}</div>}</td>
                  <td className="border-l border-slate-100 px-3 py-2.5">
                    {(e.docs || []).length === 0 ? <span className="text-slate-300">—</span> : (
                      <div className="flex flex-col gap-0.5">
                        {e.docs.map((d, i) => (
                          <a key={i} href={d.url || "#"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline" data-testid={`expense-doc-${e.id}-${i}`}>
                            <FileText className="h-3 w-3" /> {d.name || "Document"}
                          </a>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="border-l border-slate-100 px-3 py-2.5 text-right font-heading font-bold tabular-nums text-slate-900">{money(e.amount)}</td>
                  <td className="border-l border-slate-100 px-3 py-2.5">
                    <Select value={e.payment_status} onValueChange={(v) => patch(e.id, { payment_status: v })}>
                      <SelectTrigger className={`h-7 w-[130px] rounded-full border px-2.5 text-[11px] font-semibold ${PAY_META[e.payment_status]?.cls || ""}`} data-testid={`expense-payment-${e.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(PAY_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="border-l border-slate-100 px-3 py-2.5">
                    <Select value={e.status} onValueChange={(v) => patch(e.id, { status: v })}>
                      <SelectTrigger className={`h-7 w-[120px] rounded-full border px-2.5 text-[11px] font-semibold ${STATUS_META[e.status]?.cls || ""}`} data-testid={`expense-status-${e.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="border-l border-slate-100 px-2 py-2.5 whitespace-nowrap">
                    <button type="button" onClick={() => setDialog({ open: true, initial: e })} className="p-1 text-slate-400 hover:text-[#002FA7]" title="Edit" data-testid={`expense-edit-${e.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => setToDelete(e)} className="p-1 text-slate-400 hover:text-red-500" title="Delete" data-testid={`expense-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ExpenseDialog open={dialog.open} initial={dialog.initial} copackers={copackers} onClose={() => setDialog({ open: false, initial: null })} onSaved={load} />

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {toDelete?.exp_number}?</AlertDialogTitle>
            <AlertDialogDescription>This removes the expense record permanently.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="expense-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="expense-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
